import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";

export type AspectRatioPreset = "16:9" | "4:3" | "1:1" | "free";

interface CropBox {
  x: number; // % of container
  y: number;
  w: number;
  h: number;
}

interface ImageCropperProps {
  imageUrl?: string;
  onChange: (url: string) => void;
  folder?: string;
  label?: string;
  defaultAspect?: AspectRatioPreset;
}

const ASPECT_PRESETS: { key: AspectRatioPreset; label: string; icon: string; ratio: number | null }[] = [
  { key: "16:9", label: "16:9 (Banner)", icon: "ri-landscape-line", ratio: 16 / 9 },
  { key: "4:3", label: "4:3 (Tin tức)", icon: "ri-image-line", ratio: 4 / 3 },
  { key: "1:1", label: "1:1 (Logo)", icon: "ri-checkbox-blank-line", ratio: 1 },
  { key: "free", label: "Tự do", icon: "ri-crop-line", ratio: null },
];

const RESIZE_HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;
type ResizeHandle = typeof RESIZE_HANDLES[number];

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageUrl = "",
  onChange,
  folder = "images",
  label = "Ảnh",
  defaultAspect = "16:9",
}) => {
  const [src, setSrc] = useState<string>(imageUrl);
  const [localSrc, setLocalSrc] = useState<string>("");
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [aspectPreset, setAspectPreset] = useState<AspectRatioPreset>(defaultAspect);
  const [zoom, setZoom] = useState(1);
  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
  const [cropBox, setCropBox] = useState<CropBox>({ x: 10, y: 10, w: 80, h: 60 });
  const [dragging, setDragging] = useState<"crop" | ResizeHandle | "img" | null>(null);
  const [dragStart, setDragStart] = useState({ mx: 0, my: 0, box: cropBox, ox: 0, oy: 0 });
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentAspect = ASPECT_PRESETS.find((p) => p.key === aspectPreset)?.ratio ?? null;

  // Compute crop box height based on aspect ratio
  const cropH = currentAspect !== null ? cropBox.w / currentAspect : cropBox.h;

  // When aspect changes, adjust crop box height
  useEffect(() => {
    if (currentAspect !== null) {
      setCropBox((prev) => {
        const newH = prev.w / currentAspect;
        const clampedH = Math.min(newH, 90);
        const clampedW = clampedH * currentAspect;
        return { ...prev, w: Math.min(clampedW, 90), h: clampedH };
      });
    }
  }, [aspectPreset]);

  // Center crop box
  const centerCrop = useCallback(() => {
    const w = cropBox.w;
    const h = currentAspect !== null ? w / currentAspect : cropBox.h;
    setCropBox({ x: (100 - w) / 2, y: (100 - h) / 2, w, h });
  }, [cropBox.w, currentAspect]);

  // Load image natural size
  const loadImage = (url: string) => {
    const img = new Image();
    img.onload = () => setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const url = URL.createObjectURL(file);
    setLocalSrc(url);
    loadImage(url);
    setShowCropper(true);
    setZoom(1);
    setImgOffset({ x: 0, y: 0 });
    setCropBox({ x: 10, y: 10, w: 80, h: 60 });
    setPreviewUrl("");
  };

  // Mouse events for dragging
  const handleMouseDown = (e: React.MouseEvent, type: "crop" | ResizeHandle | "img") => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(type);
    setDragStart({ mx: e.clientX, my: e.clientY, box: { ...cropBox }, ox: imgOffset.x, oy: imgOffset.y });
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
      const dy = ((e.clientY - dragStart.my) / rect.height) * 100;

      if (dragging === "img") {
        setImgOffset({ x: dragStart.ox + dx, y: dragStart.oy + dy });
        return;
      }

      if (dragging === "crop") {
        const newX = Math.max(0, Math.min(100 - dragStart.box.w, dragStart.box.x + dx));
        const newY = Math.max(0, Math.min(100 - cropH, dragStart.box.y + dy));
        setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
        return;
      }

      // Resize handles
      const b = dragStart.box;
      let { x, y, w, h } = b;

      if (dragging.includes("e")) w = Math.max(10, Math.min(100 - b.x, b.w + dx));
      if (dragging.includes("w")) { x = Math.max(0, Math.min(b.x + b.w - 10, b.x + dx)); w = b.x + b.w - x; }
      if (dragging.includes("s") && currentAspect === null) h = Math.max(10, Math.min(100 - b.y, b.h + dy));
      if (dragging.includes("n") && currentAspect === null) { y = Math.max(0, Math.min(b.y + b.h - 10, b.y + dy)); h = b.y + b.h - y; }

      if (currentAspect !== null) h = w / currentAspect;
      setCropBox({ x, y, w, h });
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, dragStart, currentAspect, cropH]);

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((prev) => Math.max(0.5, Math.min(4, prev - e.deltaY * 0.001)));
  };

  // Generate realtime preview
  const generatePreview = useCallback(() => {
    if (!imgRef.current || !containerRef.current || !canvasRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const imgEl = imgRef.current;
    const imgRect = imgEl.getBoundingClientRect();

    const scaleX = imgNatural.w / imgRect.width;
    const scaleY = imgNatural.h / imgRect.height;
    const imgOffsetXPx = imgRect.left - container.left;
    const imgOffsetYPx = imgRect.top - container.top;

    const cropPxX = (cropBox.x / 100) * container.width - imgOffsetXPx;
    const cropPxY = (cropBox.y / 100) * container.height - imgOffsetYPx;
    const cropPxW = (cropBox.w / 100) * container.width;
    const cropPxH = (cropH / 100) * container.height;

    const srcX = cropPxX * scaleX;
    const srcY = cropPxY * scaleY;
    const srcW = cropPxW * scaleX;
    const srcH = cropPxH * scaleY;

    const outW = Math.min(800, Math.max(200, Math.round(srcW)));
    const outH = currentAspect !== null ? Math.round(outW / currentAspect) : Math.round(srcH);

    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.clearRect(0, 0, outW, outH);
      ctx.drawImage(img, Math.max(0, srcX), Math.max(0, srcY), srcW, srcH, 0, 0, outW, outH);
      setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = localSrc || src;
  }, [cropBox, cropH, imgNatural, currentAspect, localSrc, src]);

  // Auto-generate preview when crop changes
  useEffect(() => {
    if (!showCropper) return;
    const timer = setTimeout(generatePreview, 150);
    return () => clearTimeout(timer);
  }, [cropBox, zoom, imgOffset, showCropper, generatePreview]);

  const handleConfirm = async () => {
    if (!imgRef.current || !containerRef.current || !canvasRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const imgEl = imgRef.current;
    const imgRect = imgEl.getBoundingClientRect();

    const scaleX = imgNatural.w / imgRect.width;
    const scaleY = imgNatural.h / imgRect.height;
    const imgOffsetXPx = imgRect.left - container.left;
    const imgOffsetYPx = imgRect.top - container.top;

    const cropPxX = (cropBox.x / 100) * container.width - imgOffsetXPx;
    const cropPxY = (cropBox.y / 100) * container.height - imgOffsetYPx;
    const cropPxW = (cropBox.w / 100) * container.width;
    const cropPxH = (cropH / 100) * container.height;

    const srcX = cropPxX * scaleX;
    const srcY = cropPxY * scaleY;
    const srcW = cropPxW * scaleX;
    const srcH = cropPxH * scaleY;

    const outW = Math.min(1920, Math.max(400, Math.round(srcW)));
    const outH = currentAspect !== null ? Math.round(outW / currentAspect) : Math.round(srcH);

    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    setUploading(true);
    setUploadProgress(10);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = async () => {
      ctx.drawImage(img, Math.max(0, srcX), Math.max(0, srcY), srcW, srcH, 0, 0, outW, outH);
      canvas.toBlob(async (blob) => {
        if (!blob) { setUploading(false); return; }
        setUploadProgress(40);
        const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.webp`;
        const { data, error } = await supabase.storage
          .from("bizwell-images")
          .upload(fileName, blob, { contentType: "image/webp", upsert: false });
        setUploadProgress(80);
        if (!error && data) {
          const { data: urlData } = supabase.storage.from("bizwell-images").getPublicUrl(data.path);
          const finalUrl = urlData.publicUrl + `?t=${Date.now()}`;
          setSrc(finalUrl);
          onChange(finalUrl);
          setUploadProgress(100);
        }
        setUploading(false);
        setShowCropper(false);
        if (localSrc) { URL.revokeObjectURL(localSrc); setLocalSrc(""); }
      }, "image/webp", 0.92);
    };
    img.src = localSrc || src;
  };

  const handleCancel = () => {
    setShowCropper(false);
    if (localSrc) { URL.revokeObjectURL(localSrc); setLocalSrc(""); }
    setPreviewUrl("");
  };

  const handleRemove = () => {
    setSrc("");
    onChange("");
    setPreviewUrl("");
  };

  const cropDisplayH = currentAspect !== null ? cropBox.w / currentAspect : cropBox.h;

  return (
    <>
      <canvas ref={canvasRef} className="hidden" />

      {/* Upload zone or current image */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold text-gray-600">{label}</label>
          {src && (
            <div className="flex gap-2">
              <button type="button" onClick={() => inputRef.current?.click()} className="text-[10px] px-2 py-1 bg-amber-50 text-amber-700 rounded-full font-semibold cursor-pointer whitespace-nowrap hover:bg-amber-100">
                <i className="ri-crop-line mr-1" />Cắt lại
              </button>
              <button type="button" onClick={handleRemove} className="text-[10px] px-2 py-1 bg-red-50 text-red-600 rounded-full font-semibold cursor-pointer whitespace-nowrap hover:bg-red-100">
                <i className="ri-delete-bin-line mr-1" />Xóa
              </button>
            </div>
          )}
        </div>

        {src ? (
          <div className="relative rounded-xl overflow-hidden bg-gray-100 cursor-pointer" style={{ paddingTop: "56.25%" }} onClick={() => inputRef.current?.click()}>
            <img src={src} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-lg">Đổi ảnh</span>
            </div>
          </div>
        ) : (
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragOver ? "border-[#004A99] bg-[#EDF2FB]" : "border-gray-200 hover:border-[#004A99]/50 hover:bg-gray-50"}`}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => inputRef.current?.click()}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#EDF2FB] rounded-xl mx-auto mb-3">
              <i className="ri-image-add-line text-2xl text-[#004A99]" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Kéo thả hoặc bấm để chọn ảnh</p>
            <p className="text-xs text-gray-400">JPG, PNG, WebP · Hỗ trợ cắt ảnh thông minh</p>
          </div>
        )}
      </div>

      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />

      {/* Cropper Modal */}
      {showCropper && (localSrc || src) && (
        <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">Cắt & Chỉnh sửa ảnh</h3>
                <p className="text-xs text-gray-400 mt-0.5">Kéo khung vàng · Kéo ảnh để di chuyển · Cuộn chuột để zoom</p>
              </div>
              <button onClick={handleCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* Aspect ratio presets */}
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 mr-1">Tỷ lệ:</span>
              {ASPECT_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setAspectPreset(p.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    aspectPreset === p.key ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <i className={p.icon} />
                  {p.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={centerCrop}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-focus-3-line" />
                  Căn giữa
                </button>
              </div>
            </div>

            {/* Main crop area + preview */}
            <div className="flex flex-1 overflow-hidden min-h-0">
              {/* Crop canvas */}
              <div className="flex-1 p-4 overflow-hidden">
                <div
                  ref={containerRef}
                  className="relative w-full h-full rounded-xl overflow-hidden bg-gray-900 select-none"
                  style={{ minHeight: 300, cursor: dragging === "img" ? "grabbing" : "grab" }}
                  onMouseDown={(e) => handleMouseDown(e, "img")}
                  onWheel={handleWheel}
                >
                  {/* Image */}
                  <img
                    ref={imgRef}
                    src={localSrc || src}
                    alt="Crop"
                    className="absolute pointer-events-none"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      transform: `scale(${zoom}) translate(${imgOffset.x / zoom}%, ${imgOffset.y / zoom}%)`,
                      transformOrigin: "center center",
                    }}
                    draggable={false}
                    crossOrigin="anonymous"
                  />

                  {/* Dark overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `
                        linear-gradient(to right, rgba(0,0,0,0.55) ${cropBox.x}%, transparent ${cropBox.x}%),
                        linear-gradient(to left, rgba(0,0,0,0.55) ${100 - cropBox.x - cropBox.w}%, transparent ${100 - cropBox.x - cropBox.w}%),
                        linear-gradient(to bottom, rgba(0,0,0,0.55) ${cropBox.y}%, transparent ${cropBox.y}%),
                        linear-gradient(to top, rgba(0,0,0,0.55) ${100 - cropBox.y - cropDisplayH}%, transparent ${100 - cropBox.y - cropDisplayH}%)
                      `,
                    }}
                  />

                  {/* Crop box */}
                  <div
                    className="absolute border-2 border-yellow-400 z-10 cursor-move"
                    style={{
                      left: `${cropBox.x}%`,
                      top: `${cropBox.y}%`,
                      width: `${cropBox.w}%`,
                      height: `${cropDisplayH}%`,
                    }}
                    onMouseDown={(e) => handleMouseDown(e, "crop")}
                  >
                    {/* Rule of thirds */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/25" />
                      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/25" />
                      <div className="absolute top-1/3 left-0 right-0 h-px bg-white/25" />
                      <div className="absolute top-2/3 left-0 right-0 h-px bg-white/25" />
                    </div>

                    {/* Resize handles */}
                    {RESIZE_HANDLES.map((handle) => {
                      const posMap: Record<ResizeHandle, string> = {
                        nw: "top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nw-resize",
                        n: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-n-resize",
                        ne: "top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-ne-resize",
                        e: "top-1/2 right-0 translate-x-1/2 -translate-y-1/2 cursor-e-resize",
                        se: "bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-se-resize",
                        s: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-s-resize",
                        sw: "bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-sw-resize",
                        w: "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 cursor-w-resize",
                      };
                      return (
                        <div
                          key={handle}
                          className={`absolute w-3 h-3 bg-yellow-400 rounded-sm transform ${posMap[handle]} z-20`}
                          onMouseDown={(e) => handleMouseDown(e, handle)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right panel: preview + zoom */}
              <div className="w-56 flex-shrink-0 p-4 border-l border-gray-100 flex flex-col gap-4 overflow-y-auto">
                {/* Zoom */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600">Zoom</span>
                    <span className="text-xs font-bold text-[#004A99]">{zoom.toFixed(1)}x</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                      <i className="ri-subtract-line text-xs" />
                    </button>
                    <input type="range" min={0.5} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1 accent-[#004A99]" />
                    <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.1))} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                      <i className="ri-add-line text-xs" />
                    </button>
                  </div>
                  <button type="button" onClick={() => { setZoom(1); setImgOffset({ x: 0, y: 0 }); }} className="mt-1 text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer">Reset zoom & vị trí</button>
                </div>

                {/* Realtime preview */}
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-xs font-semibold text-gray-600">Xem trước</span>
                    <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">LIVE</span>
                  </div>
                  {previewUrl ? (
                    <div className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img src={previewUrl} alt="Preview" className="w-full object-cover" />
                    </div>
                  ) : (
                    <div className="rounded-lg bg-gray-100 flex items-center justify-center h-24 text-gray-400 text-xs">
                      Đang tạo xem trước...
                    </div>
                  )}
                  <p className="text-[9px] text-gray-400 mt-1">Cập nhật tự động khi kéo khung</p>
                </div>

                {/* Image info */}
                {imgNatural.w > 0 && (
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-[10px] font-semibold text-gray-600 mb-1">Thông tin ảnh</p>
                    <p className="text-[10px] text-gray-500">{imgNatural.w} × {imgNatural.h}px</p>
                    {imgNatural.w < 1200 && (
                      <p className="text-[10px] text-amber-600 mt-1">
                        <i className="ri-alert-line mr-0.5" />Độ phân giải thấp
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-gray-400">
                <i className="ri-information-line mr-1" />
                Cuộn chuột để zoom · Kéo ảnh để di chuyển · Kéo khung vàng để cắt
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={handleCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={uploading}
                  className="flex items-center gap-2 px-5 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#003a7a] transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <><i className="ri-loader-4-line animate-spin" />Đang tải lên... {uploadProgress}%</>
                  ) : (
                    <><i className="ri-crop-line" />Xác nhận & Lưu</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCropper;
